import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import './Tabs.css'

/**
 * Tabs Component
 *
 * A reusable tabbed interface component
 *
 * @param {Array} tabs - Array of tab objects with id, label, icon, badge, and content
 * @param {string} defaultTab - ID of the default active tab
 * @param {string} activeTab - Controlled active tab ID (optional)
 * @param {function} onTabChange - Callback when tab changes
 * @param {string} variant - Style variant: 'default', 'pills', 'underline'
 * @param {string} size - Size: 'small', 'medium', 'large'
 * @param {boolean} fullWidth - Whether tabs should take full width
 * @param {string} className - Additional CSS class
 */
const Tabs = ({
	tabs,
	defaultTab,
	activeTab: controlledActiveTab,
	onTabChange,
	variant = 'default',
	size = 'medium',
	fullWidth = false,
	className = '',
}) => {
	const [internalActiveTab, setInternalActiveTab] = useState(defaultTab || tabs[0]?.id)

	// Use controlled or internal state
	const activeTab = controlledActiveTab !== undefined ? controlledActiveTab : internalActiveTab

	const handleTabClick = useCallback(
		(tabId) => {
			if (controlledActiveTab === undefined) {
				setInternalActiveTab(tabId)
			}
			onTabChange?.(tabId)
		},
		[controlledActiveTab, onTabChange]
	)

	const activeTabContent = tabs.find((tab) => tab.id === activeTab)?.content

	return (
		<div className={`tabs tabs--${variant} tabs--${size} ${fullWidth ? 'tabs--full-width' : ''} ${className}`}>
			<div className="tabs__header" role="tablist">
				{tabs.map((tab) => {
					const isActive = activeTab === tab.id
					const Icon = tab.icon

					return (
						<button
							key={tab.id}
							className={`tabs__tab ${isActive ? 'tabs__tab--active' : ''} ${tab.disabled ? 'tabs__tab--disabled' : ''}`}
							onClick={() => !tab.disabled && handleTabClick(tab.id)}
							role="tab"
							aria-selected={isActive}
							aria-controls={`tabpanel-${tab.id}`}
							disabled={tab.disabled}
							type="button"
						>
							{Icon && (
								<span className="tabs__tab-icon">
									<Icon size={size === 'small' ? 14 : size === 'large' ? 20 : 16} />
								</span>
							)}
							<span className="tabs__tab-label">{tab.label}</span>
							{tab.badge !== undefined && (
								<span className={`tabs__tab-badge ${tab.badgeVariant ? `tabs__tab-badge--${tab.badgeVariant}` : ''}`}>
									{tab.badge}
								</span>
							)}
						</button>
					)
				})}
			</div>

			<div className="tabs__content" role="tabpanel" id={`tabpanel-${activeTab}`} aria-labelledby={activeTab}>
				{activeTabContent}
			</div>
		</div>
	)
}

Tabs.propTypes = {
	tabs: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
			icon: PropTypes.elementType,
			badge: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			badgeVariant: PropTypes.oneOf(['default', 'primary', 'success', 'warning', 'danger']),
			content: PropTypes.node,
			disabled: PropTypes.bool,
		})
	).isRequired,
	defaultTab: PropTypes.string,
	activeTab: PropTypes.string,
	onTabChange: PropTypes.func,
	variant: PropTypes.oneOf(['default', 'pills', 'underline']),
	size: PropTypes.oneOf(['small', 'medium', 'large']),
	fullWidth: PropTypes.bool,
	className: PropTypes.string,
}

export default Tabs
